import { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Image, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageUploader = ({ images = [], onChange, maxImages = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check max images
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images autorisées`);
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('token');
    const newImages = [...images];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(`${API_URL}/api/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        // Use the full URL returned by the server
        const imageUrl = res.data.url || res.data.path;
        newImages.push(imageUrl);
        toast.success(`${file.name} uploadé`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erreur upload ${file.name}: ${error.response?.data?.detail || 'Erreur inconnue'}`);
      }
    }

    onChange(newImages);
    setUploading(false);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const handleUrlAdd = (url) => {
    if (url && url.trim()) {
      onChange([...images, url.trim()]);
    }
  };

  // Get full image URL for display
  const getImageUrl = (img) => {
    if (!img) return '';
    // If it's already a full URL, use it
    if (img.startsWith('http://') || img.startsWith('https://')) {
      return img;
    }
    // If it's a relative path starting with /api/, prepend the API URL
    if (img.startsWith('/api/')) {
      return `${API_URL}${img}`;
    }
    // If it's a relative path starting with /, prepend the API URL
    if (img.startsWith('/')) {
      return `${API_URL}${img}`;
    }
    return img;
  };

  return (
    <div className="space-y-4">
      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <div key={idx} className="relative group aspect-square bg-brand-cream rounded-lg overflow-hidden border border-brand-gold/20">
              <img 
                src={getImageUrl(img)} 
                alt={`Image ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/200?text=Image';
                }}
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(idx)}
                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              {idx === 0 && (
                <span className="absolute bottom-1 left-1 bg-brand-gold text-white text-xs px-2 py-0.5 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div className="border-2 border-dashed border-brand-gold/30 rounded-lg p-6 text-center hover:border-brand-gold transition-colors bg-brand-cream">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="image-upload"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-brand-gold animate-spin" />
            <p className="text-brand-dark/60 text-sm">Upload en cours...</p>
          </div>
        ) : (
          <label htmlFor="image-upload" className="cursor-pointer block">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center">
                <Upload className="h-6 w-6 text-brand-gold" />
              </div>
              <p className="text-brand-black font-medium">Cliquez pour uploader</p>
              <p className="text-brand-dark/60 text-sm">ou glissez vos images ici</p>
              <p className="text-brand-dark/50 text-xs">Tous formats acceptés • Sans limite de taille</p>
            </div>
          </label>
        )}
      </div>

      {/* URL Input Alternative */}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Ou collez une URL d'image..."
          className="flex-1 bg-brand-white border border-brand-gold/20 rounded-lg px-3 py-2 text-brand-black text-sm placeholder:text-brand-dark/50 focus:border-brand-gold focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleUrlAdd(e.target.value);
              e.target.value = '';
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="border-brand-gold/20 text-brand-dark/80 hover:bg-brand-cream"
          onClick={(e) => {
            const input = e.target.closest('.flex').querySelector('input');
            handleUrlAdd(input.value);
            input.value = '';
          }}
        >
          <Image className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-brand-dark/50 text-xs">
        {images.length}/{maxImages} images • La première image sera l'image principale
      </p>
    </div>
  );
};
